import type { ReactNode } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import type { Icon } from '@phosphor-icons/react'
import {
  Broadcast,
  CalendarCheck,
  Package,
  WarningDiamond,
  CurrencyInr,
  Notepad,
  Tag,
  Scales,
  Bag,
  Toolbox,
  DeviceMobile,
  MapPin,
  Stack,
  Truck,
  SealCheck,
  House,
  UsersThree,
  HandCoins,
  Plant,
  BookOpen,
  TestTube,
  Factory,
  Barcode,
  Cube,
  Van,
  Flask,
  ShieldCheck,
  HandPalm,
  ArrowCounterClockwise,
  ChartLineUp,
  Buildings,
  Ruler,
  QrCode,
} from '@phosphor-icons/react'
import { ROLES, type RoleName } from '@/auth/roles'
import { RequireRole } from '@/auth/guards'
import { SessionsPage } from './pages/SessionsPage'
import { OpenAtGatePage } from './pages/OpenAtGatePage'
import { LotsPage } from './pages/LotsPage'
import { ExceptionsPage } from './pages/ExceptionsPage'
import { PayoutsPage } from './pages/PayoutsPage'
import { PaperReviewPage } from './pages/PaperReviewPage'
import { RollsPage } from './pages/RollsPage'
import { RateCardsPage } from './pages/RateCardsPage'
import { SackTypesPage } from './pages/SackTypesPage'
import { EquipmentPage } from './pages/EquipmentPage'
import { DevicesPage } from './pages/DevicesPage'
import { LocationsPage } from './pages/LocationsPage'
import { StockPage } from './pages/StockPage'
import { ShipmentsPage } from './pages/ShipmentsPage'
import { DeclarationsPage } from './pages/DeclarationsPage'
import { DashboardPage } from './pages/DashboardPage'
import { FarmerPage, FarmersPage } from './pages/FarmersPage'
import { FarmerBankPage } from './pages/FarmerBankPage'
import { FailedPayoutsPage } from './pages/payouts/FailedPayoutsPage'
import { PayAgainPage } from './pages/payouts/PayAgainPage'
import { AdvancesPage } from './pages/AdvancesPage'
import { LotDetailPage } from './pages/LotDetailPage'
import { SessionDetailPage } from './pages/SessionDetailPage'
import { ShipmentDetailPage } from './pages/ShipmentDetailPage'
import { SamplesPage } from './pages/SamplesPage'
import { YieldCapsPage } from './pages/YieldCapsPage'
import { YieldBandsPage } from './pages/YieldBandsPage'
import { ProcessRunsPage } from './pages/ProcessRunsPage'
import { ReceiptBooksPage } from './pages/ReceiptBooksPage'
import { AllocatePage } from './pages/packing/AllocatePage'
import { LabelVersionsPage } from './pages/packing/LabelVersionsPage'
import { PackLotsPage } from './pages/packing/PackLotsPage'
import { PackLotRecordPage } from './pages/packing/PackLotRecordPage'
import { DispatchesPage, DispatchRecordPage } from './pages/packing/DispatchPages'
import { LabResultsPage } from './pages/quality/LabResultsPage'
import { ReleasePage, ReleaseQueuePage } from './pages/quality/ReleasePages'
import { HoldsPage, TraceBackPage } from './pages/quality/HoldPages'
import { DisposePage } from './pages/quality/DisposePage'
import { MockRecallPage } from './pages/quality/MockRecallPage'
import { OrgsPage } from './pages/OrgsPage'
import { LabLimitsPage } from './pages/LabLimitsPage'
import { SkusPage } from './pages/SkusPage'
import { CloudWarning } from '@phosphor-icons/react'
import { SyncProblemsPage } from './pages/SyncProblemsPage'
import { RosterPrintPage } from './pages/RosterPrintPage'
import { HarvestReadinessPage } from './pages/HarvestReadinessPage'
import { QrGenerationPage } from './pages/QrGenerationPage'
import { PROCESS_DETAIL_ROUTES, PROCESS_SECTIONS } from './sections/process'
import { PACK_DETAIL_ROUTES, PACK_SECTIONS } from './sections/pack'
import { LAB_DETAIL_ROUTES, LAB_SECTIONS } from './sections/lab'
import { RECALL_DETAIL_ROUTES, RECALL_SECTIONS } from './sections/recall'

export interface ConsoleSection {
  path: string
  label: string
  icon: Icon
  group:
    | 'Overview'
    | 'Farm gate'
    | 'Payments'
    | 'Store'
    | 'Processing'
    | 'Process'
    | 'Packing'
    | 'Quality'
    | 'Pack & lab'
    | 'Setup'
  element: ReactNode
  /** Who sees it in the sidebar and may open it. Omit = every console role. */
  roles?: readonly RoleName[]
  /** Full spec only: hidden from the sidebar in Seva Mandir V1 (the V1 cuts switched it off). */
  fullOnly?: true
}

export interface ConsoleDetailRoute {
  path: string
  element: ReactNode
  roles?: readonly RoleName[]
}

const OFFICE = [ROLES.SUPERVISOR, ROLES.CENTRAL_ADMIN]
const MONEY = [ROLES.PAYMENTS_APPROVER, ROLES.SUPERVISOR]
/** Packing and quality boards (F7): the office, the supervisor and the quality-release person. */
const PACK_DESK = [ROLES.CENTRAL_ADMIN, ROLES.SUPERVISOR, ROLES.QUALITY_RELEASE]
const QUALITY = [ROLES.QUALITY_RELEASE, ROLES.SUPERVISOR, ROLES.CENTRAL_ADMIN]
/** Labs/buyers and lab limits: roles holding orgs:read / lab-limits:read (central_admin creates). */
const LAB_SETUP = [ROLES.CENTRAL_ADMIN, ROLES.QUALITY_RELEASE]

/**
 * The console's single source of truth: sidebar entries AND routes come from this list.
 * To add a page: create it in ./pages and add one entry here.
 */
export const CONSOLE_SECTIONS: ConsoleSection[] = [
  { path: 'today', label: 'Today', icon: House, group: 'Overview', element: <DashboardPage /> },
  {
    path: 'sessions',
    label: 'Collection days',
    icon: CalendarCheck,
    group: 'Farm gate',
    element: <SessionsPage />,
  },
  {
    path: 'open-at-gate',
    label: 'Open at the gate',
    icon: Broadcast,
    group: 'Farm gate',
    element: <OpenAtGatePage />,
  },
  { path: 'lots', label: 'Lots', icon: Package, group: 'Farm gate', element: <LotsPage /> },
  {
    path: 'exceptions',
    label: 'Exceptions',
    icon: WarningDiamond,
    group: 'Farm gate',
    element: <ExceptionsPage />,
  },

  {
    path: 'paper-review',
    label: 'Paper review',
    icon: Notepad,
    group: 'Farm gate',
    element: <PaperReviewPage />,
    roles: OFFICE,
    fullOnly: true,
  },
  {
    path: 'rolls',
    label: 'Rolls & tags',
    icon: Tag,
    group: 'Farm gate',
    element: <RollsPage />,
    roles: OFFICE,
  },
  {
    path: 'qr-generation',
    label: 'Generate QR codes',
    icon: QrCode,
    group: 'Farm gate',
    element: <QrGenerationPage />,
    roles: [ROLES.CENTRAL_ADMIN, ROLES.ADMIN],
  },
  {
    path: 'receipt-books',
    label: 'Receipt books',
    icon: BookOpen,
    group: 'Farm gate',
    element: <ReceiptBooksPage />,
    roles: OFFICE,
    fullOnly: true,
  },
  {
    path: 'payouts',
    label: 'Payouts',
    icon: CurrencyInr,
    group: 'Payments',
    element: <PayoutsPage />,
    roles: MONEY,
  },
  {
    path: 'harvest-readiness',
    label: 'Ready for harvest',
    icon: Plant,
    group: 'Farm gate',
    element: <HarvestReadinessPage />,
    roles: [ROLES.SUPERVISOR, ROLES.CENTRAL_ADMIN],
  },
  {
    path: 'farmers',
    label: 'Farmers',
    icon: UsersThree,
    group: 'Farm gate',
    element: <FarmersPage />,
    roles: [...MONEY, ROLES.CENTRAL_ADMIN],
  },
  {
    path: 'advances',
    label: 'Advances import',
    icon: HandCoins,
    group: 'Payments',
    element: <AdvancesPage />,
    roles: [ROLES.PAYMENTS_APPROVER],
    fullOnly: true,
  },
  {
    path: 'locations',
    label: 'Locations',
    icon: MapPin,
    group: 'Store',
    element: <LocationsPage />,
  },
  { path: 'stock', label: 'Stock', icon: Stack, group: 'Store', element: <StockPage /> },
  {
    path: 'shipments',
    label: 'Shipments',
    icon: Truck,
    group: 'Store',
    element: <ShipmentsPage />,
  },
  { path: 'samples', label: 'Samples', icon: TestTube, group: 'Store', element: <SamplesPage /> },
  {
    path: 'declarations',
    label: 'Declarations',
    icon: SealCheck,
    group: 'Store',
    element: <DeclarationsPage />,
  },
  // Machine processing log (pilot): every run per machine, filterable; process-runs:read roles.
  {
    path: 'processing/runs',
    label: 'Processing runs',
    icon: Factory,
    group: 'Processing',
    element: <ProcessRunsPage />,
    roles: QUALITY,
  },
  // Processing setup (F6-B1.c): machine yield bands; a machine's steps are set on Equipment.
  {
    path: 'processing/yield-bands',
    label: 'Yield bands',
    icon: ChartLineUp,
    group: 'Processing',
    element: <YieldBandsPage />,
    roles: OFFICE,
  },
  // V1 console boards of F7 (alignment map V1-F7-01a … V1-F7-B1c).
  {
    path: 'packing/allocate',
    label: 'Allocate',
    icon: Factory,
    group: 'Packing',
    element: <AllocatePage />,
    roles: [ROLES.CENTRAL_ADMIN],
  },
  {
    path: 'packing/labels',
    label: 'Label versions',
    icon: Barcode,
    group: 'Packing',
    element: <LabelVersionsPage />,
    roles: PACK_DESK,
  },
  {
    path: 'packing/lots',
    label: 'Pack lots',
    icon: Cube,
    group: 'Packing',
    element: <PackLotsPage />,
    roles: PACK_DESK,
  },
  {
    path: 'packing/dispatches',
    label: 'Dispatches',
    icon: Van,
    group: 'Packing',
    element: <DispatchesPage />,
    roles: PACK_DESK,
  },
  {
    path: 'quality/lab-results',
    label: 'Lab results',
    icon: Flask,
    group: 'Quality',
    element: <LabResultsPage />,
    roles: QUALITY,
  },
  {
    path: 'quality/release',
    label: 'Release',
    icon: ShieldCheck,
    group: 'Quality',
    element: <ReleaseQueuePage />,
    roles: QUALITY,
  },
  {
    path: 'quality/holds',
    label: 'Lab holds',
    icon: HandPalm,
    group: 'Quality',
    element: <HoldsPage />,
    roles: QUALITY,
  },
  {
    path: 'quality/recall',
    label: 'Mock recall',
    icon: ArrowCounterClockwise,
    group: 'Quality',
    element: <MockRecallPage />,
    roles: QUALITY,
  },
  // The full-spec console areas merged from main stay hidden in the V1 pilot.
  ...PROCESS_SECTIONS.map((section) => ({ ...section, fullOnly: true as const })),
  ...PACK_SECTIONS.map((section) => ({ ...section, fullOnly: true as const })),
  ...LAB_SECTIONS.map((section) => ({ ...section, fullOnly: true as const })),
  ...RECALL_SECTIONS.map((section) => ({ ...section, fullOnly: true as const })),
  {
    path: 'rate-cards',
    label: 'Rate cards',
    icon: Scales,
    group: 'Setup',
    element: <RateCardsPage />,
    roles: OFFICE,
  },
  {
    path: 'sack-types',
    label: 'Sack types',
    icon: Bag,
    group: 'Setup',
    element: <SackTypesPage />,
    roles: OFFICE,
  },
  {
    path: 'yield-caps',
    label: 'Yield caps',
    icon: Plant,
    group: 'Setup',
    element: <YieldCapsPage />,
    roles: OFFICE,
  },
  {
    path: 'equipment',
    label: 'Equipment',
    icon: Toolbox,
    group: 'Setup',
    element: <EquipmentPage />,
    roles: OFFICE,
  },
  // Pilot master data (orgs, lab limits, products) that had an API but no screen.
  {
    path: 'setup/orgs',
    label: 'Labs & buyers',
    icon: Buildings,
    group: 'Setup',
    element: <OrgsPage />,
    roles: LAB_SETUP,
  },
  {
    path: 'setup/lab-limits',
    label: 'Lab limits',
    icon: Ruler,
    group: 'Setup',
    element: <LabLimitsPage />,
    roles: LAB_SETUP,
  },
  {
    path: 'setup/products',
    label: 'Products',
    icon: Package,
    group: 'Setup',
    element: <SkusPage />,
    roles: PACK_DESK,
  },
  {
    path: 'devices',
    label: 'Devices',
    icon: DeviceMobile,
    group: 'Setup',
    element: <DevicesPage />,
    roles: [ROLES.CENTRAL_ADMIN],
  },
  // Phone entries the server did not apply (pilot: no engineer needed to see or clear them).
  {
    path: 'sync-problems',
    label: 'Sync problems',
    icon: CloudWarning,
    group: 'Setup',
    element: <SyncProblemsPage />,
    roles: OFFICE,
  },
]

/** Detail pages: routed, not in the sidebar (the sidebar highlights their list). */
const DETAIL_ROUTES: ConsoleDetailRoute[] = [
  { path: 'sessions/:id', element: <SessionDetailPage /> },
  { path: 'lots/:id', element: <LotDetailPage /> },
  { path: 'shipments/:id', element: <ShipmentDetailPage /> },
  {
    path: 'farmers/roster/:groupId',
    element: <RosterPrintPage />,
    roles: [...MONEY, ROLES.CENTRAL_ADMIN],
  },
  { path: 'farmers/:id', element: <FarmerPage />, roles: [...MONEY, ROLES.CENTRAL_ADMIN] },
  // V1 console boards (alignment map F4-B4.a/b/c).
  { path: 'payouts/failed', element: <FailedPayoutsPage />, roles: MONEY },
  { path: 'payouts/:id/pay-again', element: <PayAgainPage />, roles: MONEY },
  { path: 'farmers/:id/bank', element: <FarmerBankPage />, roles: MONEY },
  // V1 console boards of F7 (V1-F7-04n, 08k, 06a/b, B1b, B1c).
  { path: 'packing/lots/:id', element: <PackLotRecordPage />, roles: PACK_DESK },
  { path: 'packing/dispatches/:id', element: <DispatchRecordPage />, roles: PACK_DESK },
  { path: 'quality/release/:id', element: <ReleasePage />, roles: QUALITY },
  { path: 'quality/holds/:unitId/trace-back', element: <TraceBackPage />, roles: QUALITY },
  { path: 'quality/holds/:unitId/dispose', element: <DisposePage />, roles: QUALITY },
  ...PROCESS_DETAIL_ROUTES,
  ...PACK_DETAIL_ROUTES,
  ...LAB_DETAIL_ROUTES,
  ...RECALL_DETAIL_ROUTES,
]

const guard = (element: ReactNode, roles?: readonly RoleName[]) =>
  roles ? <RequireRole roles={roles}>{element}</RequireRole> : element

export const consoleRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="today" replace /> },
  ...CONSOLE_SECTIONS.map<RouteObject>((s) => ({
    path: s.path,
    element: guard(s.element, s.roles),
  })),
  ...DETAIL_ROUTES.map<RouteObject>((r) => ({ path: r.path, element: guard(r.element, r.roles) })),
  {
    path: 'replacements',
    element: <Navigate to="/c/exceptions?type=CONTAINER_REPLACEMENT&status=OPEN" replace />,
  },
]
